/**
 * Wundrlst design tokens
 * Single source of truth for visual constants used across the app.
 * Import from here instead of writing raw Tailwind strings inline.
 *
 * **Composable UI:** Base primitives live in `src/components/ui/` (shadcn “radix-nova”).
 * Rich data views often build on **Kibo UI** (`src/components/kibo-ui/`, registry `@kibo-ui`
 * in components.json). Supplemental layouts may use **shadcn-space** (`src/components/shadcn-space/`).
 * Phase numbering for the visual reset is documented in `docs/VISUAL_SYSTEM_PHASES.md`.
 */

// ─── Typography ───────────────────────────────────────────────────────────────

export const heading = {
  /** Page-level title. Use on <h1> in every app page. */
  h1: "text-2xl font-semibold tracking-tight text-foreground md:text-3xl",
  /** Section title inside a page or card. Use on <h2>. */
  h2: "text-lg font-semibold tracking-tight text-foreground",
  /** Sub-section or card group label. Use on <h3>. */
  h3: "text-base font-semibold text-foreground",
  /** Eyebrow / category label above a section. NOT a heading tag. */
  sectionLabel: "text-xs font-medium uppercase tracking-widest text-muted-foreground",
  /** Card metric value (default — KPI cards, dashboard tiles). */
  metricValue: "text-2xl font-semibold tabular-nums tracking-tight",
  /** Headline metric value (page-level totals — Finances overview, etc.). Reach for this only when the metric is the page's primary number. */
  metricValueLg: "text-3xl font-semibold tabular-nums tracking-tight",
  /** Card metric label above the value. */
  metricLabel: "text-sm font-medium text-muted-foreground",
  /** Card-style `<h2>` / `<h3>` inside marketing feature grids. */
  marketingCardTitle: "text-xl font-semibold tracking-tight text-foreground",
} as const

/** Body copy scales for marketing and dense UI. */
export const body = {
  /** Intro paragraph under hero or section intros. */
  lead: "text-base leading-relaxed text-muted-foreground lg:text-lg",
  /** Larger marketing section subtitle (e.g. features grid intro). */
  leadLg: "text-lg font-medium text-muted-foreground lg:text-xl",
} as const

/**
 * Landing / marketing page typography (public site, not app shell).
 * Prefer these over duplicating long Tailwind strings in section components.
 */
export const marketing = {
  eyebrow: "text-xs font-medium uppercase tracking-widest text-primary",
  heroTitle:
    "text-balance text-4xl font-semibold tracking-tight text-foreground md:text-5xl lg:text-6xl",
  heroSubtitle: "text-base leading-relaxed text-muted-foreground lg:text-lg",
  /** Default large section title (pricing, FAQ, etc.). */
  sectionTitle: "text-balance text-3xl font-semibold tracking-tight text-foreground md:text-4xl lg:text-5xl",
  /** Features grid section `<h2>` (max width + large type). */
  sectionTitleWide:
    "max-w-3xl text-balance text-3xl font-semibold tracking-tight text-foreground lg:text-5xl",
  blogHeading:
    "mb-3 text-balance text-4xl font-semibold tracking-tighter text-foreground md:mb-4 md:text-5xl lg:mb-6 lg:max-w-3xl lg:text-6xl",
  journeysTitle: "mt-4 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl md:text-4xl",
  journeysSubtitle:
    "mx-auto mt-4 max-w-2xl text-balance text-base leading-relaxed text-muted-foreground md:text-lg",
  testimonialsTitle:
    "mt-3 text-balance text-2xl font-semibold tracking-tight text-foreground md:text-3xl lg:text-4xl",
  faqTitle: "text-balance text-3xl font-semibold tracking-tight text-foreground md:text-4xl",
  personasTitle: "text-3xl font-medium tracking-tight text-foreground md:text-4xl",
  personasSubtitle: "mt-4 text-lg font-medium text-muted-foreground md:text-xl",
  ctaHeading:
    "text-balance text-3xl font-semibold tracking-tight text-primary-foreground lg:text-4xl",
  ctaHeadingFaded: "text-primary-foreground/70",
  ctaSubtitle: "mx-auto max-w-xl text-balance text-lg text-primary-foreground/80",
} as const

// ─── Icon Sizes ───────────────────────────────────────────────────────────────

export const icon = {
  /** Tiny indicator dot / badge icon */
  xs: "size-3",
  /** Inline icon in dense text or small badges */
  sm: "size-3.5",
  /** Standard inline icon — DEFAULT for most use cases */
  md: "size-4",
  /** Emphasis icon in action buttons or list rows */
  lg: "size-5",
  /** Section header icon or feature icon */
  xl: "size-6",
  /** Empty-state illustration icon */
  emptyState: "size-8",
  /** Hero / large feature icon */
  hero: "size-12",
  /** Marketing feature grid: icon wrapper (e.g. FeaturesSeven). */
  featureWrap: "size-10",
} as const

// ─── Spacing ──────────────────────────────────────────────────────────────────
// Allowed gap/space values. Do not use gap-5, space-y-5, or arbitrary values.

export const spacing = {
  /** Between tightly related items (badge groups, icon+label) */
  xs: "gap-2",
  /** Between form fields or button groups */
  sm: "gap-3",
  /** Standard card/section internal spacing */
  md: "gap-4",
  /** Between major sections within a page */
  lg: "gap-6",
  /** Between top-level page blocks */
  xl: "gap-8",
} as const

export const stack = {
  xs: "space-y-2",
  sm: "space-y-3",
  md: "space-y-4",
  lg: "space-y-6",
  xl: "space-y-8",
} as const

// ─── Status Colors ────────────────────────────────────────────────────────────
// Use these for urgency, progress, and state indicators.
// Always include both light and dark variants.

export const statusText = {
  ok:      "text-green-600 dark:text-green-400",
  warning: "text-amber-600 dark:text-amber-400",
  danger:  "text-red-600 dark:text-red-400",
  info:    "text-blue-500 dark:text-blue-400",
  neutral: "text-muted-foreground",
} as const

export const statusBg = {
  ok:      "bg-green-50 border-green-200 dark:bg-green-950/40 dark:border-green-800/50",
  warning: "bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800/50",
  danger:  "bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-800/50",
  info:    "bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:border-blue-800/50",
  neutral: "bg-muted border-border",
} as const

// ─── Progress Bar ─────────────────────────────────────────────────────────────
// Apply as className on shadcn <Progress> to color the indicator track.

export const progressColor = {
  ok:      "[&_[data-slot=progress-indicator]]:bg-green-600",
  warning: "[&_[data-slot=progress-indicator]]:bg-amber-500",
  danger:  "[&_[data-slot=progress-indicator]]:bg-red-500",
} as const

/** Solid fill for custom div bars (e.g. confidence) — same semantics as `progressColor`. */
export const progressFill = {
  ok:      "bg-green-600 dark:bg-green-500",
  warning: "bg-amber-500 dark:bg-amber-400",
  danger:  "bg-red-500 dark:bg-red-400",
} as const

/** Returns the correct progressColor key based on a 0–100 percentage. */
export function getProgressColorClass(pct: number): string {
  if (pct >= 100) return progressColor.danger
  if (pct >= 75)  return progressColor.warning
  return progressColor.ok
}

// ─── Button Conventions ───────────────────────────────────────────────────────
// Not Tailwind classes — these are usage rules for <Button> props.
//
//  Primary CTA          → variant="default",     size=(default)
//  Secondary / cancel   → variant="outline",      size=(default) or size="sm"
//  Destructive action   → variant="destructive",  size=(default)
//  Icon-only action     → variant="ghost",        size="icon"
//  Inline / form action → any variant,            size="sm"
//  Navigation link btn  → variant="ghost",        size=(default)

// ─── Border Radius ────────────────────────────────────────────────────────────
// Always use shadcn CSS var. Never hardcode rounded-* values on shadcn components.
// Use rounded-xl ONLY for custom non-shadcn containers (onboarding cards, etc.)

export const radius = {
  /** Matches --radius from CSS vars (shadcn default) */
  default: "rounded-[var(--radius)]",
  /** For non-shadcn containers that need explicit xl radius */
  card: "rounded-xl",
} as const

// ─── Shadows ──────────────────────────────────────────────────────────────────
// Prefer this scale over ad-hoc shadow-* in marketing and cards.

export const shadow = {
  none: "shadow-none",
  sm: "shadow-sm",
  md: "shadow-md",
  lg: "shadow-lg",
} as const
